import { FC, HTMLProps, ReactNode } from 'react';

import { useComponentId } from '@telialt/utils/src/hooks';

import { Body, Card, CheckedSymbol, Image, IndeterminateSymbol, Indicator, Main, Title } from './CheckboxCard.styles';
import { CheckboxPosition, ImageFitVariant } from './model';

type Inherited = Omit<HTMLProps<HTMLInputElement>, 'title' | 'type'>;

interface Props extends Inherited {
    checkboxPosition?: CheckboxPosition;
    imageFit?: ImageFitVariant;
    image?: { src: string; altText: string };
    title: ReactNode;
    body?: ReactNode;
    children?: ReactNode;
    'data-test'?: string;
}

export const CheckboxCard: FC<Props> = ({
    id,
    title,
    body,
    children,
    disabled,
    className,
    style,
    hidden,
    checked,
    readOnly,
    image,
    checkboxPosition = 'right',
    imageFit = 'outset',
    'data-test': dataTest = 'checkbox-card',
    ...htmlInputProps
}) => {
    const UID = useComponentId('checkbox-card');
    const ID = {
        CHECKBOX: id || UID,
        UNAVAILABLE: UID + '--unavailable',
        DESCRIPTION: UID + '--description',
    };
    const TEST_ID = { INPUT: `${dataTest}--input`, IMAGE: `${dataTest}--image` };

    return (
        <Card className={className} style={style} hidden={hidden} $checkboxPosition={checkboxPosition}>
            <input
                aria-labelledby={ID.DESCRIPTION}
                {...htmlInputProps}
                id={ID.CHECKBOX}
                type="checkbox"
                disabled={disabled}
                checked={checked}
                readOnly={readOnly}
                data-test={TEST_ID.INPUT}
            />
            <label htmlFor={ID.CHECKBOX}>
                {!!image && (
                    <Image
                        src={image.src}
                        alt={image.altText}
                        $imageFit={imageFit}
                        $checkboxPosition={checkboxPosition}
                        data-test={TEST_ID.IMAGE}
                    />
                )}
                <Indicator $checkboxPosition={checkboxPosition}>
                    <IndeterminateSymbol />
                    <CheckedSymbol />
                </Indicator>
                <Main id={ID.DESCRIPTION} $checkboxPosition={checkboxPosition}>
                    {typeof title === 'string' ? <Title>{title}</Title> : title}
                    {typeof body === 'string' ? <Body>{body}</Body> : body}
                </Main>
            </label>
            {children}
        </Card>
    );
};
